import {
	budgetPeriodFor,
	buildWeeklyMemoryStory,
	type DayClose,
	deriveDayDogam,
	formatKstDate,
	formatWon,
	type HouseholdConsumptionState,
	kstDateKey,
	resolveLivingBudgetPlan,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { monthStartDayPref, readPref, writePref } from "./prefs";
import { PURCHASE_REASON_LABELS } from "./purchase-labels";
export function WeeklyCloseSummary({
	receipts,
	closes,
	household,
}: {
	receipts: Receipt[];
	closes: DayClose[];
	household: HouseholdConsumptionState;
}) {
	const [compare, setCompare] = useState(
		readPref("sobidogam:weeklyCompare") !== "off",
	);
	const now = new Date();
	const today = Date.parse(`${kstDateKey(now)}T00:00:00+09:00`);
	const weekday = new Date(today + 9 * 3600000).getUTCDay();
	const start = today - ((weekday + 6) % 7) * 86400000;
	const end = start + 7 * 86400000;
	const records = receipts.filter(
		(r) =>
			!r.deletedAt &&
			Date.parse(r.transactedAt) >= start &&
			Date.parse(r.transactedAt) < Math.min(end, today + 86400000),
	);
	const total = records.reduce((s, r) => s + r.totalAmount, 0);
	const byDay = new Map<string, number>();
	for (const r of records) {
		const d = kstDateKey(r.transactedAt);
		byDay.set(d, (byDay.get(d) ?? 0) + r.totalAmount);
	}
	const top = [...byDay].sort((a, b) => b[1] - a[1])[0];
	const count = closes.filter(
		(c) =>
			Date.parse(`${c.date}T00:00:00+09:00`) >= start &&
			Date.parse(`${c.date}T00:00:00+09:00`) < end,
	).length;
	const monthStartDay = monthStartDayPref();
	const anchors = [new Date(start), now];
	const periods = new Map(
		anchors.map((anchor) => [
			budgetPeriodFor(anchor, monthStartDay).key,
			anchor,
		]),
	);
	const days = [...periods.values()].flatMap(
		(anchor) =>
			deriveDayDogam(
				receipts,
				resolveLivingBudgetPlan(household, anchor, monthStartDay),
				household.recurringPayments ?? [],
				closes,
				anchor,
				now,
			).days,
	);
	const zero = days.filter((d) => {
		const time = Date.parse(`${d.date}T00:00:00+09:00`);
		return d.kind === "zero" && time >= start && time < end;
	}).length;
	const previous = receipts
		.filter(
			(r) =>
				!r.deletedAt &&
				Date.parse(r.transactedAt) >= start - 7 * 86400000 &&
				Date.parse(r.transactedAt) <
					start - 7 * 86400000 + (Math.min(end, today + 86400000) - start),
		)
		.reduce((s, r) => s + r.totalAmount, 0);
	const story = buildWeeklyMemoryStory(receipts);
	const topMemory = top
		? records.find(
				(r) =>
					kstDateKey(r.transactedAt) === top[0] &&
					(r.purchaseReason || r.expectedScene?.trim()),
			)
		: undefined;
	const topReason = [
		topMemory?.purchaseReason
			? PURCHASE_REASON_LABELS[topMemory.purchaseReason]
			: null,
		topMemory?.expectedScene?.trim(),
	]
		.filter(Boolean)
		.join(" · ");
	return (
		<section className="weekly-close-summary">
			<h2>이번 주 정리</h2>
			<p>
				{records.length}건 · {formatWon(total)}
			</p>
			<p>
				마감한 날 {count}일 · 0원 쓴 날 {zero}일
			</p>
			{top ? (
				<p>
					가장 많이 쓴 날은 {formatKstDate(`${top[0]}T12:00:00+09:00`)} ·{" "}
					{formatWon(top[1])}
					{topReason ? ` · ${topReason}` : " · 남겨 둔 이유가 없어요."}
				</p>
			) : (
				<p>기록을 남기면 이번 주 장면이 여기에 모여요.</p>
			)}
			<label>
				비교 대상
				<select
					value={compare ? "me" : "off"}
					onChange={(e) => {
						const next = e.target.value === "me";
						setCompare(next);
						writePref("sobidogam:weeklyCompare", next ? "me" : "off");
					}}
				>
					<option value="me">지난 기간의 나</option>
					<option value="off">끄기</option>
				</select>
			</label>
			{compare ? (
				<p>
					지난주 같은 기간보다 {formatWon(Math.abs(total - previous))}{" "}
					{total >= previous ? "더" : "덜"} 기록했어요.
				</p>
			) : null}
			<p className="field-hint">남과 비교하는 순위는 없어요.</p>
			<h3>{story.title}</h3>
			<p>{story.body}</p>
			<blockquote>{story.prompt}</blockquote>
		</section>
	);
}
